/**
 * Reusable Recharts wrappers, dark-themed (MASTER §7). All take already-shaped
 * data + formatter callbacks so pages stay declarative. A table fallback for a11y
 * lives in each consuming page.
 */
import type { ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const AXIS = 'rgb(148 163 184)';
const GRID = 'rgba(41,53,72,0.6)';

/** Distinct categorical hues (colorblind-aware ordering). */
export const SERIES_COLORS = [
  '#F97316',
  '#22C55E',
  '#38BDF8',
  '#A78BFA',
  '#F59E0B',
  '#EF4444',
  '#14B8A6',
  '#EC4899',
];

/** Definite series color by index (avoids undefined under noUncheckedIndexedAccess). */
export function seriesColor(i: number): string {
  return SERIES_COLORS[i % SERIES_COLORS.length] ?? '#F97316';
}

function tooltipStyle(): {
  contentStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  itemStyle: React.CSSProperties;
} {
  return {
    contentStyle: {
      background: 'rgb(17 24 39)',
      border: '1px solid rgb(41 53 72)',
      borderRadius: 10,
      fontFamily: 'Barlow, system-ui, sans-serif',
      fontSize: 13,
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
    },
    labelStyle: { color: 'rgb(148 163 184)', fontWeight: 600, marginBottom: 4 },
    itemStyle: { color: 'rgb(248 250 252)' },
  };
}

export interface TrendPoint {
  label: string;
  [series: string]: string | number;
}

/** Time-trend area/line chart; one or more series (MASTER §7 line/area). */
export function TrendChart({
  data,
  series,
  formatValue,
}: {
  data: TrendPoint[];
  series: { key: string; name: string; color?: string; area?: boolean }[];
  formatValue?: (v: number) => string;
}): ReactNode {
  const fmtProp = formatValue ? { tickFormatter: (v: number) => formatValue(v) } : {};
  const tipFmt = formatValue ? { formatter: (v: unknown) => formatValue(Number(v)) } : {};
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
        <defs>
          {series.map((s, i) => {
            const c = s.color ?? seriesColor(i);
            return (
              <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c} stopOpacity={0.22} />
                <stop offset="100%" stopColor={c} stopOpacity={0} />
              </linearGradient>
            );
          })}
        </defs>
        <XAxis
          dataKey="label"
          stroke={AXIS}
          tickLine={false}
          axisLine={{ stroke: GRID }}
          fontSize={12}
        />
        <YAxis
          stroke={AXIS}
          tickLine={false}
          axisLine={false}
          fontSize={12}
          width={52}
          {...fmtProp}
        />
        <Tooltip {...tooltipStyle()} {...tipFmt} cursor={{ stroke: GRID }} />
        {series.length > 1 ? <Legend wrapperStyle={{ fontSize: 12, color: AXIS }} /> : null}
        {series.map((s, i) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color ?? seriesColor(i)}
            strokeWidth={2}
            fill={s.area === false ? 'transparent' : `url(#grad-${s.key})`}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export interface RankedDatum {
  label: string;
  value: number;
  color?: string;
}

/** Horizontal bar, sorted desc, distinct hues, value labels (MASTER §7 bar). */
export function RankedBarChart({
  data,
  formatValue,
}: {
  data: RankedDatum[];
  formatValue?: (v: number) => string;
}): ReactNode {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const tipFmt = formatValue ? { formatter: (v: unknown) => formatValue(Number(v)) } : {};
  const labelFmt = formatValue ? { formatter: (v: unknown) => formatValue(Number(v)) } : {};
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={sorted} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="label"
          stroke={AXIS}
          tickLine={false}
          axisLine={false}
          fontSize={12}
          width={120}
        />
        <Tooltip {...tooltipStyle()} cursor={{ fill: 'rgba(255,255,255,0.04)' }} {...tipFmt} />
        <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={20}>
          {sorted.map((d, i) => (
            <Cell key={d.label} fill={d.color ?? seriesColor(i)} />
          ))}
          <LabelList dataKey="value" position="right" fontSize={12} fill={AXIS} {...labelFmt} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export interface DonutDatum {
  label: string;
  value: number;
  color: string;
}

/** Donut with a centered total (MASTER §7 donut). */
export function DonutChart({
  data,
  centerLabel,
  centerValue,
}: {
  data: DonutDatum[];
  centerLabel?: ReactNode;
  centerValue?: ReactNode;
}): ReactNode {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="relative h-full w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={total === 0 ? [{ label: '—', value: 1, color: 'rgb(31 41 55)' }] : data}
            dataKey="value"
            nameKey="label"
            innerRadius="62%"
            outerRadius="90%"
            paddingAngle={total === 0 ? 0 : 2}
            stroke="rgb(17 24 39)"
            strokeWidth={2}
          >
            {(total === 0 ? [{ color: 'rgb(31 41 55)' }] : data).map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
          <Tooltip {...tooltipStyle()} />
          <Legend
            verticalAlign="bottom"
            iconType="circle"
            wrapperStyle={{ fontSize: 12, color: AXIS }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-x-0 top-[40%] -translate-y-1/2 text-center">
        <div className="font-display text-2xl font-bold text-fg">{centerValue}</div>
        {centerLabel ? (
          <div className="text-xs uppercase tracking-wide text-muted">{centerLabel}</div>
        ) : null}
      </div>
    </div>
  );
}

export interface HeatCell {
  day: number; // 0..6
  hour: number; // 0..23
  value: number;
}

const HEAT_DAYS_MK = ['Пон', 'Вто', 'Сре', 'Чет', 'Пет', 'Саб', 'Нед'];

/** Occupancy density heatmap, cool→hot (MASTER §7 heatmap). */
export function OccupancyHeatmap({
  cells,
  hours = Array.from({ length: 17 }, (_, i) => i + 6), // 06:00–22:00
  dayLabels = HEAT_DAYS_MK,
}: {
  cells: HeatCell[];
  hours?: number[];
  dayLabels?: string[];
}): ReactNode {
  const map = new Map<string, number>();
  let max = 0;
  for (const c of cells) {
    map.set(`${c.day}-${c.hour}`, c.value);
    if (c.value > max) max = c.value;
  }
  const tone = (v: number): string => {
    if (max === 0 || v === 0) return 'rgb(var(--surface-2))';
    const t = v / max; // 0..1 cool→hot
    // interpolate info-blue (56,189,248) → warning (245,158,11) → danger (239,68,68)
    const lerp = (a: number, b: number, k: number): number => Math.round(a + (b - a) * k);
    let r: number, g: number, b: number;
    if (t < 0.5) {
      const k = t / 0.5;
      r = lerp(56, 245, k);
      g = lerp(189, 158, k);
      b = lerp(248, 11, k);
    } else {
      const k = (t - 0.5) / 0.5;
      r = lerp(245, 239, k);
      g = lerp(158, 68, k);
      b = lerp(11, 68, k);
    }
    return `rgb(${r} ${g} ${b} / ${0.35 + t * 0.65})`;
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-x-auto">
        <div
          className="grid gap-1"
          style={{ gridTemplateColumns: `2.5rem repeat(${hours.length}, minmax(0,1fr))` }}
        >
          <div />
          {hours.map((h) => (
            <div key={h} className="text-center text-[10px] text-faint">
              {h}
            </div>
          ))}
          {dayLabels.map((d, di) => (
            <div key={d} className="contents">
              <div className="flex items-center text-[11px] font-medium text-muted">{d}</div>
              {hours.map((h) => {
                const v = map.get(`${di}-${h}`) ?? 0;
                return (
                  <div
                    key={h}
                    title={`${d} ${h}:00 — ${v}`}
                    className="aspect-square rounded-[3px]"
                    style={{ background: tone(v) }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-muted">
        <span>0</span>
        <div
          className="h-2 w-28 rounded-full"
          style={{
            background:
              'linear-gradient(90deg, rgb(var(--surface-2)), rgb(56 189 248), rgb(245 158 11), rgb(239 68 68))',
          }}
        />
        <span>{max}</span>
      </div>
    </div>
  );
}
